import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FiBookOpen, FiPlusCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ClassDetailPage from './ClassDetailPage';
import LanguageSelector from '../components/LanguageSelector'

const TeacherDashboard = ({ setUser, setActiveRole }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('myClasses');
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [editingClass, setEditingClass] = useState(null);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const roles = user?.roles || [user?.role];
  const activeRole = localStorage.getItem('activeRole');

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('activeRole');
    setUser(null);
    setActiveRole(null);
    navigate('/');
  };

  const handleSwitchRole = () => {
    const newRole = roles.find((r) => r !== activeRole);
    localStorage.setItem('activeRole', newRole);
    setActiveRole(newRole);
    navigate(newRole === 'teacher' ? '/teacher' : '/student-dashboard');
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get('https://amora-backend.onrender.com/api/classes');
      setClasses(res.data);
    } catch (err) {
      console.error('Failed to load classes', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'myClasses') {
      fetchClasses();
    }
  }, [activeTab]);

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}`);
      fetchClasses();
      if (selectedClassId === classId) setSelectedClassId(null);
    } catch (err) {
      console.error('Error deleting class:', err);
    }
  };

  const handleEditClass = (cls) => {
    setEditingClass(cls);
  };

  const handleSaveEditedClass = async (updated) => {
    try {
      await axios.put(`https://amora-backend.onrender.com/api/classes/${updated._id}`, updated);
      setEditingClass(null);
      fetchClasses();
    } catch (err) {
      console.error('Failed to update class:', err);
    }
  };

  const renderContent = () => {
    if (selectedClassId) {
      return (
        <ClassDetailPage
          classId={selectedClassId}
          onBack={() => setSelectedClassId(null)}
        />
      );
    }

    if (editingClass) {
      return (
        <div className="p-8 bg-gray-900 text-white min-h-screen">
          <h2 className="text-2xl font-semibold mb-6 text-purple-400">{t('dashboard.editTitle')}</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveEditedClass(editingClass);
            }}
            className="space-y-4 max-w-xl"
          >
            <input
              type="text"
              className="w-full px-4 py-3 rounded bg-gray-800 text-white border border-gray-600"
              value={editingClass.title}
              onChange={(e) => setEditingClass({ ...editingClass, title: e.target.value })}
              placeholder={t('dashboard.classTitle')}
            />
            <input
              type="text"
              className="w-full px-4 py-3 rounded bg-gray-800 text-white border border-gray-600"
              value={editingClass.subject}
              onChange={(e) => setEditingClass({ ...editingClass, subject: e.target.value })}
              placeholder={t('dashboard.subject')}
            />
            <input
              type="text"
              className="w-full px-4 py-3 rounded bg-gray-800 text-white border border-gray-600"
              value={editingClass.yearGroup}
              onChange={(e) => setEditingClass({ ...editingClass, yearGroup: e.target.value })}
              placeholder={t('dashboard.yearGroup')}
            />
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded text-white font-semibold"
            >
              ✅ {t('dashboard.saveChanges')}
            </button>
            <button
              onClick={() => setEditingClass(null)}
              className="ml-4 text-red-400 underline"
            >
              {t('dashboard.cancel')}
            </button>
          </form>
        </div>
      );
    }

    switch (activeTab) {
      case 'myClasses':
        return (
          <div className="p-8 bg-gray-900 text-white min-h-screen">
            <h2 className="text-2xl font-semibold mb-6 text-purple-400">📚 {t('dashboard.myClasses')}</h2>

            {classes.length === 0 ? (
              <div className="text-center mt-24">
                <p className="text-lg text-gray-400 mb-4">{t('dashboard.noClasses')}</p>
                <button
                  onClick={() => setActiveTab('createClass')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition"
                >
                  {t('dashboard.createFirst')}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {classes.map((cls) => (
                  <div
                    key={cls._id}
                    className="p-6 border border-gray-700 rounded-lg shadow-sm bg-gray-800 hover:shadow-md transition"
                  >
                    <h3 className="text-xl font-bold mb-1 text-white">{cls.title}</h3>
                    <p className="text-gray-400 mb-2">{cls.yearGroup} • {cls.subject}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      🧾 {t('dashboard.classCode')}: <span className="font-mono">{cls._id}</span>
                    </p>
                    <div className="flex gap-4 mt-3">
                      <button
                        onClick={() => setSelectedClassId(cls._id)}
                        className="text-blue-400 hover:underline"
                      >
                        {t('dashboard.view')}
                      </button>
                      <button
                        onClick={() => handleEditClass(cls)}
                        className="text-yellow-400 hover:underline text-sm"
                      >
                        {t('dashboard.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteClass(cls._id)}
                        className="text-red-400 hover:underline"
                      >
                        {t('dashboard.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'createClass':
        return (
          <div className="p-8 bg-gray-900 text-white min-h-screen">
            <h2 className="text-2xl font-semibold mb-6 text-purple-400">✏️ {t('dashboard.createTitle')}</h2>
            <form
              className="space-y-6 max-w-lg"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const newClass = {
                  title: form.title.value,
                  description: form.description.value,
                  subject: form.subject.value,
                  yearGroup: form.yearGroup.value,
                };
                await axios.post('https://amora-backend.onrender.com/api/classes', newClass);
                form.reset();
                setActiveTab('myClasses');
                fetchClasses();
              }}
            >
              <input name="title" type="text" placeholder={t('dashboard.classTitle')} className="w-full px-4 py-3 border border-gray-700 rounded bg-gray-800 text-white" />
              <textarea name="description" placeholder={t('dashboard.description')} className="w-full px-4 py-3 border border-gray-700 rounded bg-gray-800 text-white" />
              <input name="subject" type="text" placeholder={t('dashboard.subject')} className="w-full px-4 py-3 border border-gray-700 rounded bg-gray-800 text-white" />
              <input name="yearGroup" type="text" placeholder={t('dashboard.yearGroup')} className="w-full px-4 py-3 border border-gray-700 rounded bg-gray-800 text-white" />
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 transition text-white px-6 py-2 rounded font-semibold">
                {t('dashboard.createButton')}
              </button>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="absolute top-4 right-6 flex gap-4 z-50">
      <LanguageSelector /> {/* ✅ Added here */}
        {roles.length > 1 && (
          <button
            onClick={handleSwitchRole}
            className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded text-sm font-semibold"
          >
            {t('dashboard.switchTo', {
  role: t(`roles.${roles.find((r) => r !== activeRole)}`)
})}
          </button>
        )}
        <button
          onClick={handleLogout}
          className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm font-semibold"
        >
          {t('dashboard.logout')}
        </button>
      </div>

      <aside className="w-64 bg-gray-900 text-white flex flex-col py-8 px-6 space-y-4">
        <h1 className="text-2xl font-bold mb-8">{t('dashboard.teacherTitle')}</h1>

        <button onClick={() => { setActiveTab('myClasses'); setSelectedClassId(null); }} className={`flex items-center gap-2 px-4 py-2 rounded ${activeTab === 'myClasses' ? 'bg-blue-700' : 'hover:bg-gray-800'}`}>
          <FiBookOpen /> {t('dashboard.myClasses')}
        </button>

        <button onClick={() => { setActiveTab('createClass'); setSelectedClassId(null); }} className={`flex items-center gap-2 px-4 py-2 rounded ${activeTab === 'createClass' ? 'bg-blue-700' : 'hover:bg-gray-800'}`}>
          <FiPlusCircle /> {t('dashboard.createClass')}
        </button>
      </aside>

      <main className="flex-1 overflow-y-auto">
        {renderContent()}
      </main>
    </div>
  );
};

export default TeacherDashboard;
